/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { StatisticsGroupBy, StatisticsTab } from "~/features/statistics/constants";
import { StatisticsTabConfig } from "~/features/statistics/types";

const statisticsTabConfigMap: { [key in StatisticsTab]: StatisticsTabConfig } = {
  [StatisticsTab.TOTAL_STATS]: {
    columnKeys: [
      "sitedomain",
      "clicks",
      "uniquelogins",
      "nonprejns",
      "prejns",
      "newchrgusr",
      "abvthreshusr",
      "trstamntsuc",
      "chrgbkamnt",
      "chargebackcount",
      "chrgbkcramnt",
      "netrevenue",
      "pptype",
      "netpayout",
    ],
    activeColumns: {
      sitedomain: true, // Domain
      clicks: true, // Clicks
      uniquelogins: false, // Unique logins
      nonprejns: false, // Non-premium joins
      prejns: true, // Premium joins
      newchrgusr: true, // New paying users
      abvthreshusr: false, // High spenders
      trstamntsuc: true, // Gross revenue
      chrgbkamnt: true, // Chargeback
      chargebackcount: false, // Chargebacks count
      chrgbkcramnt: true, // Refund
      netrevenue: true, // Net revenue
      pptype: true, // Program
      netpayout: true, // Estimated payout
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [
      StatisticsGroupBy.DATE,
      StatisticsGroupBy.TRACKING_ID,
      StatisticsGroupBy.REFERRER_ID,
      StatisticsGroupBy.PROCESSORID,
    ],
  },
  [StatisticsTab.PLATFORM_STATS]: {
    columnKeys: [
      "dev",
      "clicks",
      "nonprejns",
      "prejns",
      "newchrgusr",
      "abvthreshusr",
      "trstamntsuc",
      "chrgbkamnt",
      "chargebackcount",
      "chrgbkcramnt",
      "netrevenue",
    ],
    activeColumns: {
      dev: true, // Platform
      clicks: true, // Clicks
      nonprejns: false, // Non-premium joins
      prejns: true, // Premium joins
      newchrgusr: true, // Nes paying users
      abvthreshusr: false, // High spenders
      trstamntsuc: true, // Gross revenue
      chrgbkamnt: true, // Chargeback
      chargebackcount: false, // Chargebacks count
      chrgbkcramnt: true, // Refund
      netrevenue: true, // Net revenue
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE, StatisticsGroupBy.TRACKING_ID],
  },
  [StatisticsTab.USER_VALUE]: {
    columnKeys: [
      "sitedomain",
      "prejns",
      "usrspndcntlt",
      "usrspndprcntlt",
      "ltval",
      "usrspndcnt24",
      "trstprcntsuc24",
      "trstamntsuc24",
      "spendermembervalue24",
      "usrspndcnt7",
      "trstprcntsuc7",
      "trstamntsuc7",
      "spendermembervalue7",
      "usrspndcnt30",
      "trstprcntsuc30",
      "trstamntsuc30",
      "spendermembervalue30",
      "usrspndcnt31p",
      "trstprcntsuc31p",
      "trstamntsuc31p",
    ],
    activeColumns: {
      sitedomain: true,
      prejns: true,
      spender_totals: true,
      day1: true,
      day27: true,
      day830: true,
      days31p: true,
    },
    excludedSites: true,
    deviceFilter: true,
    trackingId: true,
    enabledGroups: [
      StatisticsGroupBy.DATE,
      StatisticsGroupBy.TRACKING_ID,
      StatisticsGroupBy.COUNTRY,
      StatisticsGroupBy.PLATFORM,
    ],
    columnGroup: {
      sitedomain: ["sitedomain"],
      prejns: ["prejns"],
      spender_totals: ["usrspndcntlt", "usrspndprcntlt", "ltval"],
      day1: ["usrspndcnt24", "trstprcntsuc24", "trstamntsuc24", "spendermembervalue24"],
      day27: ["usrspndcnt7", "trstprcntsuc7", "trstamntsuc7", "spendermembervalue7"],
      day830: ["usrspndcnt30", "trstprcntsuc30", "trstamntsuc30", "spendermembervalue30"],
      days31p: ["usrspndcnt31p", "trstprcntsuc31p", "trstamntsuc31p"],
    },
  },
  [StatisticsTab.GEO]: {
    columnKeys: [
      "ctry",
      "clicks",
      "nonprejns",
      "prejns",
      "newchrgusr",
      "abvthreshusr",
      "trstamntsuc",
      "chrgbkamnt",
      "chargebackcount",
      "chrgbkcramnt",
      "netrevenue",
    ],
    activeColumns: {
      ctry: true, // Country
      clicks: true, // Clicks
      nonprejns: false, // Non-premium joins
      prejns: true, // Premium joins
      newchrgusr: true, // New paying users
      abvthreshusr: false, // High spenders
      trstamntsuc: true, // Gross revenue
      chrgbkamnt: true, // Chargeback
      chargebackcount: false, // Chargebacks count
      chrgbkcramnt: true, // Refund
      netrevenue: true, // Net revenue
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE, StatisticsGroupBy.TRACKING_ID],
  },
  [StatisticsTab.UPSELL_STATS]: {
    columnKeys: [
      "sitedomain",
      "upsell_sales_amount",
      "total_adjustment",
      "upsell_sales_count",
      "upsell_chargeback_count",
      "upsell_cancel_count",
      "upsell_net_sales",
      "upsell_reseller_payout",
    ],
    activeColumns: {
      sitedomain: true, // Domain
      upsell_sales_amount: true, // Gross revenue
      total_adjustment: true, // Total adjustment
      upsell_sales_count: true, // Subscribers
      upsell_chargeback_count: true, // Chargeback count
      upsell_cancel_count: true, // Cancels count
      upsell_net_sales: true, // Net revenue
      upsell_reseller_payout: true, // Estimated payout
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.PAST_PAYOUTS]: {
    columnKeys: ["payment_date", "pay_method", "payment_amount", "pay_period"],
    activeColumns: {
      payment_date: true,
      pay_method: true,
      payment_amount: true,
      pay_period: true,
    },
    columnGroup: {},
    excludedSites: false,
    deviceFilter: false,
    trackingId: false,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.USER_SPENDING]: {
    columnKeys: ["userid", "ctry", "dev", "activity", "model", "amount", "referrerkey", "trackingid"],
    activeColumns: {
      userid: true,
      ctry: true,
      dev: true,
      activity: true,
      model: true,
      amount: true,
      referrerkey: true,
      trackingid: true,
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: false,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
  [StatisticsTab.VIDEO_SALES]: {
    columnKeys: [
      "domain",
      "videoRevSharePercentage",
      "videoSalesCount",
      "totalVideoSales",
      "estimatedVideoSalesRevenue",
    ],
    activeColumns: {
      domain: true,
      videoRevSharePercentage: true,
      videoSalesCount: true,
      totalVideoSales: true,
      estimatedVideoSalesRevenue: true,
    },
    columnGroup: {},
    excludedSites: true,
    deviceFilter: false,
    trackingId: true,
    enabledGroups: [StatisticsGroupBy.DATE],
  },
};

export default statisticsTabConfigMap;
